import {
  DEFAULT_PRODUCTION_SPECIES,
  PRODUCTION_SPECIES_OPTIONS,
  getPresetColumns,
  type ProductionSpeciesKey
} from '../../data/productionSpeciesPresets'
import type { EntityMutationSyncState } from '../../utils/entityMutationSync'
import type { ImportSourceFile } from '../../utils/importTable'
import type { BiomassDataRow } from '../../utils/productionUnitTable'
import type { WaterDataRow } from '../../utils/waterTable'
import {
  buildProductionSummaryImportSessionFromCsvText,
  type ProductionSummaryImportSession
} from '../datatable/productionSummaryImport'
import {
  buildFeedImportSessionFromCsvText,
  type FeedImportSession
} from '../feed/feedImport'
import {
  buildMortalityImportSessionFromCsvText,
  type MortalityImportSession
} from '../biomass/mortalityImport'
import {
  buildWaterImportSessionFromCsvText,
  type WaterImportSession
} from '../water/waterImport'
import type { FeedLog, MortalityLog } from '../../types/production'

export type ImportWorkspaceTargetKey = 'water' | 'productionDatatable' | 'feed' | 'mortality'
export type ImportWorkspaceSessionSnapshot = WaterImportSession | ProductionSummaryImportSession | FeedImportSession | MortalityImportSession

export type ImportWorkspaceLot = {
  id: string
  lotKey: string
  stage?: string
}

export type ImportWorkspaceContext = {
  farmId: string
  productionUnitId: string
  speciesKey?: string
}

export type ImportWorkspaceRouteState = {
  target: ImportWorkspaceTargetKey
  farmId: string
  productionUnitId: string
  speciesKey: ProductionSpeciesKey
}

export type ImportWorkspaceReviewRouteState = {
  farmId: string
  productionUnitId: string
  speciesKey: ProductionSpeciesKey
  importSource: string
  importedRows: number
  skippedRows: number
  queuedRows: number
  localOnlyRows: number
  blockedRows: number
  syncErrorMessage: string | null
  syncState: EntityMutationSyncState | null
}

export const IMPORT_WORKSPACE_TARGET_OPTIONS: Array<{ key: ImportWorkspaceTargetKey; label: string }> = [
  { key: 'water', label: 'Water' },
  { key: 'productionDatatable', label: 'Production summary' },
  { key: 'feed', label: 'Feed' },
  { key: 'mortality', label: 'Mortality' }
]

const EXCLUDED_PRODUCTION_COLUMNS = new Set(['id', 'lotId', 'unmappedLot', 'archived'])

const appendUniqueColumn = (nextColumn: string, seen: Set<string>, output: string[]) => {
  const trimmed = String(nextColumn ?? '').trim()
  if (!trimmed || seen.has(trimmed) || EXCLUDED_PRODUCTION_COLUMNS.has(trimmed)) return
  seen.add(trimmed)
  output.push(trimmed)
}

export const buildImportWorkspaceProductionColumns = (args: {
  existingRows: BiomassDataRow[]
  speciesKey: string
}): string[] => {
  const seen = new Set<string>()
  const output: string[] = []

  getPresetColumns(args.speciesKey).forEach((column) => appendUniqueColumn(column, seen, output))
  args.existingRows.forEach((row) => {
    Object.keys(row).forEach((key) => appendUniqueColumn(key, seen, output))
  })

  return output
}

export const toImportSourceFile = (file: File): ImportSourceFile => ({
  name: file.name,
  format: 'csv',
  sizeBytes: file.size,
  mimeType: file.type || undefined,
  lastModifiedMs: Number.isFinite(file.lastModified) ? file.lastModified : undefined
})

const normalizeProductionSpeciesKey = (value: string | undefined): ProductionSpeciesKey => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (PRODUCTION_SPECIES_OPTIONS.some((option) => option.key === normalized)) {
    return normalized as ProductionSpeciesKey
  }
  return DEFAULT_PRODUCTION_SPECIES
}

const parsePositiveCount = (value: string | null | undefined): number => {
  const parsed = Number.parseInt(String(value ?? '').trim(), 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

const normalizeSyncErrorMessage = (value: string | null | undefined): string | null => {
  const compact = String(value ?? '').replace(/\s+/g, ' ').trim()
  if (!compact) return null
  if (compact.length <= 160) return compact
  return `${compact.slice(0, 157).trimEnd()}...`
}

const normalizeReviewSyncState = (value: string | null | undefined): EntityMutationSyncState | null => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === 'offline' || normalized === 'pending' || normalized === 'synced') {
    return normalized
  }
  return null
}

const normalizeImportWorkspaceTarget = (value: string | undefined): ImportWorkspaceTargetKey => {
  return IMPORT_WORKSPACE_TARGET_OPTIONS.some((option) => option.key === value)
    ? (value as ImportWorkspaceTargetKey)
    : 'water'
}

export const parseImportWorkspaceRouteState = (search: string): ImportWorkspaceRouteState => {
  const params = new URLSearchParams(search)
  return {
    target: normalizeImportWorkspaceTarget(params.get('target') ?? undefined),
    farmId: params.get('farmId')?.trim() ?? '',
    productionUnitId: params.get('productionUnitId')?.trim() ?? '',
    speciesKey: normalizeProductionSpeciesKey(params.get('speciesKey') ?? undefined)
  }
}

export const parseImportWorkspaceReviewRouteState = (search: string): ImportWorkspaceReviewRouteState => {
  const params = new URLSearchParams(search)
  return {
    farmId: params.get('farmId')?.trim() ?? '',
    productionUnitId: params.get('productionUnitId')?.trim() ?? '',
    speciesKey: normalizeProductionSpeciesKey(params.get('speciesKey') ?? undefined),
    importSource: params.get('importSource')?.trim() ?? '',
    importedRows: parsePositiveCount(params.get('importedRows')),
    skippedRows: parsePositiveCount(params.get('skippedRows')),
    queuedRows: parsePositiveCount(params.get('queuedRows')),
    localOnlyRows: parsePositiveCount(params.get('localOnlyRows')),
    blockedRows: parsePositiveCount(params.get('blockedRows')),
    syncErrorMessage: normalizeSyncErrorMessage(params.get('syncError')),
    syncState: normalizeReviewSyncState(params.get('syncState'))
  }
}

export const buildImportWorkspacePath = (args: {
  target: ImportWorkspaceTargetKey
  context?: ImportWorkspaceContext | null
}): string => {
  const params = new URLSearchParams()
  params.set('target', args.target)

  const farmId = args.context?.farmId?.trim() ?? ''
  const productionUnitId = args.context?.productionUnitId?.trim() ?? ''
  const speciesKey = normalizeProductionSpeciesKey(args.context?.speciesKey)

  if (farmId) params.set('farmId', farmId)
  if (productionUnitId) params.set('productionUnitId', productionUnitId)
  if (args.target === 'productionDatatable') params.set('speciesKey', speciesKey)

  return `/imports?${params.toString()}`
}

export const buildImportWorkspaceReviewPath = (args: {
  target: ImportWorkspaceTargetKey
  context?: ImportWorkspaceContext | null
  importedRows: number
  skippedRows: number
  queuedRows?: number
  localOnlyRows?: number
  blockedRows?: number
  syncErrorMessage?: string | null
  syncState?: EntityMutationSyncState | null
}): string => {
  const params = new URLSearchParams()
  const farmId = args.context?.farmId?.trim() ?? ''
  const productionUnitId = args.context?.productionUnitId?.trim() ?? ''
  const speciesKey = normalizeProductionSpeciesKey(args.context?.speciesKey)

  if (farmId) params.set('farmId', farmId)
  if (productionUnitId) params.set('productionUnitId', productionUnitId)
  if (args.target === 'productionDatatable') params.set('speciesKey', speciesKey)
  params.set('importSource', 'imports')
  params.set('importedRows', String(Math.max(0, args.importedRows)))
  if (args.skippedRows > 0) params.set('skippedRows', String(args.skippedRows))
  if ((args.queuedRows ?? 0) > 0) params.set('queuedRows', String(args.queuedRows))
  if ((args.localOnlyRows ?? 0) > 0) params.set('localOnlyRows', String(args.localOnlyRows))
  if ((args.blockedRows ?? 0) > 0) params.set('blockedRows', String(args.blockedRows))
  const syncErrorMessage = normalizeSyncErrorMessage(args.syncErrorMessage)
  if ((args.blockedRows ?? 0) > 0 && syncErrorMessage) params.set('syncError', syncErrorMessage)
  if (args.syncState) params.set('syncState', args.syncState)

  let path = '/database'
  if (args.target === 'water') {
    path = '/water'
  } else if (args.target === 'feed') {
    path = '/feed'
  } else if (args.target === 'mortality') {
    path = '/biomass'
  }
  return `${path}?${params.toString()}`
}

const pluralSuffix = (count: number): string => (count === 1 ? '' : 's')

const singularOrPluralVerb = (count: number, singular: string, plural: string): string => {
  return count === 1 ? singular : plural
}

const buildQueuedSyncNotice = (args: {
  queuedRows: number
  syncState?: EntityMutationSyncState | null
}): string | null => {
  if (args.queuedRows <= 0) return null

  const rowLabel = `row${pluralSuffix(args.queuedRows)}`
  if (args.syncState === 'offline') {
    return `${args.queuedRows} ${rowLabel} ${singularOrPluralVerb(args.queuedRows, 'was', 'were')} saved offline and will sync when the device is online.`
  }
  if (args.syncState === 'synced') {
    return `${args.queuedRows} ${rowLabel} ${singularOrPluralVerb(args.queuedRows, 'has', 'have')} already synced.`
  }
  return `${args.queuedRows} ${rowLabel} ${singularOrPluralVerb(args.queuedRows, 'was', 'were')} queued for sync.`
}

const buildBlockedSyncNotice = (args: {
  blockedRows: number
  syncErrorMessage?: string | null
}): string | null => {
  if (args.blockedRows <= 0) return null

  const detail = normalizeSyncErrorMessage(args.syncErrorMessage)
  const message = `${args.blockedRows} queued row${pluralSuffix(args.blockedRows)} ${singularOrPluralVerb(args.blockedRows, 'is', 'are')} still blocked by sync.`
  return detail ? `${message} Blocking error: ${detail}` : message
}

const buildLocalOnlyNotice = (localOnlyRows: number): string | null => {
  if (localOnlyRows <= 0) return null
  return `${localOnlyRows} row${pluralSuffix(localOnlyRows)} remained local-only and ${singularOrPluralVerb(localOnlyRows, 'was', 'were')} not queued for sync.`
}

const buildSkippedNotice = (skippedRows: number): string | null => {
  if (skippedRows <= 0) return null
  return `${skippedRows} row${pluralSuffix(skippedRows)} ${singularOrPluralVerb(skippedRows, 'was', 'were')} skipped during workspace review.`
}

export const buildImportWorkspaceReviewNotice = (args: {
  rowLabel: string
  importedRows: number
  skippedRows: number
  queuedRows?: number
  localOnlyRows?: number
  blockedRows?: number
  syncErrorMessage?: string | null
  syncState?: EntityMutationSyncState | null
}): string | null => {
  if (args.importedRows <= 0) return null

  const queuedRows = Math.max(0, args.queuedRows ?? 0)
  const localOnlyRows = Math.max(0, args.localOnlyRows ?? 0)
  const parts = [
    `Imported ${args.importedRows} ${args.rowLabel}${pluralSuffix(args.importedRows)} from Import Workspace.`,
    buildQueuedSyncNotice({ queuedRows, syncState: args.syncState }),
    buildBlockedSyncNotice({
      blockedRows: Math.max(0, args.blockedRows ?? 0),
      syncErrorMessage: args.syncErrorMessage
    }),
    buildLocalOnlyNotice(localOnlyRows),
    buildSkippedNotice(args.skippedRows)
  ].filter((value): value is string => Boolean(value))

  return parts.join(' ')
}

export const buildImportWorkspaceSession = (args: {
  target: ImportWorkspaceTargetKey
  text: string
  sourceFile?: ImportSourceFile | null
  context: ImportWorkspaceContext
  availableLots: ImportWorkspaceLot[]
  waterRows: WaterDataRow[]
  productionRows: BiomassDataRow[]
  feedRows: FeedLog[]
  mortalityRows: MortalityLog[]
  /**
   * v2.1 #47: farm timezone (IANA) used to derive `(lot, day)` dedup keys
   * during preview. When omitted, the legacy UTC-prefix behavior is preserved.
   * Callers that know the farm should pass it; ImportWorkspace.tsx resolves
   * the timezone from the selected farm (falling back to the browser TZ).
   */
  timeZone?: string
}): ImportWorkspaceSessionSnapshot | null => {
  const normalizedLots = args.availableLots.map((lot) => ({
    id: String(lot.id),
    lotKey: String(lot.lotKey ?? ''),
    stage: lot.stage
  }))
  const lotKeyById = new Map(normalizedLots.map((lot) => [lot.id, lot.lotKey] as const))

  if (args.target === 'water') {
    return buildWaterImportSessionFromCsvText({
      text: args.text,
      existingRows: args.waterRows,
      availableLots: normalizedLots,
      lotKeyById,
      sourceFile: args.sourceFile ?? null,
      assignedContext: {
        farmId: args.context.farmId,
        productionUnitId: args.context.productionUnitId
      },
      timeZone: args.timeZone
    })
  }

  if (args.target === 'feed') {
    return buildFeedImportSessionFromCsvText({
      text: args.text,
      existingRows: args.feedRows,
      availableLots: normalizedLots,
      lotKeyById,
      sourceFile: args.sourceFile ?? null,
      assignedContext: {
        farmId: args.context.farmId,
        productionUnitId: args.context.productionUnitId
      },
      timeZone: args.timeZone
    })
  }

  if (args.target === 'mortality') {
    return buildMortalityImportSessionFromCsvText({
      text: args.text,
      existingRows: args.mortalityRows.map((row) => ({
        id: row.id,
        farmId: row.farmId,
        productionUnitId: row.productionUnitId,
        recordedAt: row.recordedAt,
        ...(row.lot ? { lot: row.lot } : {}),
        ...(row.lotId ? { lotId: row.lotId } : {}),
        mortalityCount: row.mortalityCount,
        ...(row.notes ? { notes: row.notes } : {}),
        ...(row.metadata ? { metadata: row.metadata } : {})
      })),
      availableLots: normalizedLots,
      lotKeyById,
      sourceFile: args.sourceFile ?? null,
      assignedContext: {
        farmId: args.context.farmId,
        productionUnitId: args.context.productionUnitId
      },
      timeZone: args.timeZone
    })
  }

  const speciesKey = normalizeProductionSpeciesKey(args.context.speciesKey)
  return buildProductionSummaryImportSessionFromCsvText({
    text: args.text,
    activeColumns: buildImportWorkspaceProductionColumns({
      existingRows: args.productionRows,
      speciesKey
    }),
    speciesKey,
    existingRows: args.productionRows,
    availableLots: normalizedLots,
    lotKeyById,
    sourceFile: args.sourceFile ?? null,
    assignedContext: {
      farmId: args.context.farmId,
      productionUnitId: args.context.productionUnitId
    },
    timeZone: args.timeZone
  })
}